import * as React from "react";

import { Checkbox as CheckboxPrimitive } from "radix-ui";

import { cn } from "@/utils/cn";

/**
 * Standard checkbox control. Unchecked it sits on the same surface as `Input`,
 * so it reads as part of the form; checked it fills with the button color and a
 * white tick. Borderless, and it switches instantly rather than animating, to
 * match the other controls.
 *
 * The tick is drawn inline rather than via the `Icon` primitive because Solar
 * has no bare checkmark — only ticks already baked into a square or circle,
 * which fight the box this control draws itself. Being the control's own
 * indicator (like the caret in an input), it takes its color from `currentColor`
 * so the token classes below still drive the theme.
 */
function Checkbox({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer size-5 shrink-0 rounded-sm bg-tg-section-bg outline-none",
        "data-[state=checked]:bg-tg-button data-[state=checked]:text-tg-button-text",
        "focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-3.5"
          aria-hidden
        >
          <path d="M5 12.5 10 17.5 19 7" />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
